<script>
export default {
  name: 'BaseSummaryCard',
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    styleCard: {
      type: String,
      default: 'flatGray',
      validator: (value) =>
        ['flatGray', 'shadowWhite', 'bordered'].includes(value),
    },
    // Remove a sombra do card `bordered` (borda hairline apenas). Backward
    // compatible: default false mantém o `shadow-sm` do DS nos consumidores atuais.
    noShadow: { type: Boolean, default: false },
  },
  computed: {
    cardClass() {
      const isBordered = this.styleCard === 'bordered'
      return {
        'd-flex': true,
        'justify-center': true,
        'summary-card': true,
        'summary-card--loading': this.loading,
        'flat-gray': this.styleCard === 'flatGray',
        'shadow-white': this.styleCard === 'shadowWhite',
        bordered: isBordered,
        // Skin (fundo/borda/raio/sombra) do card bordered vem do DS.
        'az-card-bordered': isBordered,
        // Modifier do DS que zera o box-shadow do az-card-bordered.
        'no-shadow': isBordered && this.noShadow,
      }
    },
  },
  methods: {
    getValueClass(card) {
      return card.class ? card.class : 'text-color-800'
    },
    onCardClick(card) {
      if (!card || !card.clickable) return
      this.$emit('card-click', card)
    },
  },
}
</script>

<template>
  <div class="d-flex summary-container">
    <div
      v-for="card in items"
      :key="card.key"
      :class="[cardClass, { 'summary-card--clickable': card.clickable }]"
      :role="card.clickable ? 'button' : null"
      :tabindex="card.clickable ? 0 : null"
      :aria-label="card.clickable ? card.ariaLabel : null"
      @click="onCardClick(card)"
      @keydown.enter="onCardClick(card)"
      @keydown.space.prevent="onCardClick(card)"
    >
      <template v-if="loading">
        <!-- min-height próprio garante o skeleton visível mesmo no card bordered
             (altura auto) — sem isso o loading "some". -->
        <BaseLoadingSkeleton
          height="100%"
          class="d-flex w-100 h-100"
          style="min-height: 72px"
        />
      </template>

      <!-- Layout DS (Geist/shadcn): ícone primary + rótulo no topo, número
           grande embaixo, texto auxiliar. Usado no card bordered (padrão CRM). -->
      <template v-else-if="styleCard === 'bordered'">
        <div class="summary-card__head">
          <span class="az-ds-body">{{ card.label }}</span>
          <v-tooltip v-if="card.tooltip" right max-width="360">
            <template v-slot:activator="{ on, attrs }">
              <icon-svg
                :name="card.icon || 'mdi-information'"
                size="16"
                color="var(--muted)"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <span>{{ card.tooltip }}</span>
          </v-tooltip>
          <icon-svg
            v-else-if="card.icon"
            :name="card.icon"
            :size="16"
            color="var(--muted)"
          />
        </div>

        <span :class="card.class" class="az-ds-kpi">{{ card.value }}</span>

        <!-- Rodapé sempre presente (min-height reserva a linha do auxiliar) p/ o
             valor ficar na mesma posição tenha ou não texto auxiliar. -->
        <div class="summary-card__foot">
          <span v-if="card.variation" class="az-ds-badge-positive">
            {{ card.variation }}
          </span>
          <span v-if="card.description" class="az-ds-aux">
            {{ card.description }}
          </span>
        </div>

        <span
          v-if="card.observation"
          class="az-ds-aux summary-card__observation"
          :class="card.observationClass"
        >
          {{ card.observation }}
        </span>
      </template>

      <!-- Layout antigo (modais: flatGray / shadowWhite). -->
      <template v-else>
        <div
          class="d-flex justify-space-around align-center flex-column justify-center px-4"
          :class="card.description ? 'py-4' : 'py-6'"
        >
          <span class="summary-title text-center text-color-800">
            {{ card.label }}</span>

          <span class="summary-subtitle text-center text-color-800">
            {{ card.description }}
          </span>

          <span
            :class="getValueClass(card)"
            class="az-paragraph font-semibold text-center"
          >
            {{ card.value }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.summary-container
  flex-wrap: wrap
  gap: 24px
  width: 100%
  justify-content: center
  align-items: stretch
  box-sizing: border-box

.summary-card
  flex: 1
  min-width: 160px
  height: 120px
  border-radius: 8px
  text-align: center
  box-sizing: border-box
  overflow: hidden

  // No loading o skeleton preenche o card inteiro (sem o gap da padding).
  &--loading
    padding: 0 !important

  &.flat-gray
    background-color: #f0f1f3

  &.shadow-white
    background-color: var(--white)
    box-shadow: 0 0 16px rgba(0, 0, 0, 0.12)

  // Skin (fundo/borda/raio/sombra) vem de .az-card-bordered (DS). Aqui só o
  // layout/padding proprios do card de sumario.
  &.bordered
    flex-direction: column
    // Ancora no topo: icone/rotulo/valor ficam SEMPRE na mesma posicao, tenham
    // ou nao texto auxiliar embaixo (sem space-between, que reposicionava).
    justify-content: flex-start
    align-items: stretch
    height: auto
    // Casa com a altura do card preenchido (KPI 32px) p/ loading e item terem a
    // mesma altura (antes 124 -> loading ficava menor que o item ~131).
    min-height: 132px
    min-width: 200px
    border-radius: var(--radius-card)
    text-align: left
    padding: 20px
    gap: 12px

.summary-card--clickable
  cursor: pointer

  &:focus-visible
    outline: 2px solid var(--border-focus)
    outline-offset: 2px

.summary-card__observation
  width: 100%

.summary-card__head
  display: flex
  align-items: center
  justify-content: space-between
  width: 100%

.summary-card__foot
  display: flex
  align-items: center
  gap: 8px
  // Reserva a altura de uma linha de auxiliar mesmo quando vazio.
  min-height: 16px

// Rótulo/valor/auxiliar usam as classes do DS (az-ds-card-title / az-ds-kpi /
// az-ds-aux) e o badge de variação usa .az-ds-badge-positive — em az-text.sass.

.summary-title
  font-weight: 600
  font-size: 16px

.summary-subtitle
  font-weight: 400
  font-size: 14px

@media (max-width: 600px)
  .summary-container
    flex-direction: column
    gap: 12px

  .summary-card
    width: 100%
    min-width: unset
    padding: 12px
</style>
